import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { Stethoscope, Lightbulb, HelpCircle, ClipboardList, Building2, UserPlus } from 'lucide-react';
import ConsultModal from './ConsultModal';

const typeCfg = {
  diagnosis:   { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-100',    dot: 'bg-red-400',    Icon: Stethoscope },
  suggestion:  { bg: 'bg-sky-50',    text: 'text-sky-700',    border: 'border-sky-100',    dot: 'bg-sky-400',    Icon: Lightbulb },
  question:    { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-100',  dot: 'bg-amber-400',  Icon: HelpCircle },
  observation: { bg: 'bg-slate-50',  text: 'text-slate-600',  border: 'border-slate-100',  dot: 'bg-slate-400',  Icon: ClipboardList },
};

const MedicalDiscussion = ({ symptom, onAddInsight, symptomId, consultedDoctorIds = [], onConsultSuccess }) => {
  const [newInsight, setNewInsight] = useState('');
  const [insightType, setInsightType] = useState('observation');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consultModalOpen, setConsultModalOpen] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newInsight.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onAddInsight({ text: newInsight, type: insightType });
      setNewInsight('');
      setInsightType('observation');
    } catch (error) {
      console.error('Failed to add insight:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const responses = symptom?.responses || [];

  return (
    <div className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 border border-outline-variant/10 overflow-hidden relative">
      {/* Decorative gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.02] to-transparent pointer-events-none" />
      {/* Decorative blur */}
      <div className="absolute -top-16 -right-16 w-40 h-40 bg-primary/5 rounded-full blur-[80px] pointer-events-none" />

      <div className="p-7 space-y-6 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Collaborative Review</p>
              <h3 className="font-headline text-xl font-bold text-slate-900 tracking-tight">Medical Discussion</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'DOCTOR' && symptomId && (
              <button
                onClick={() => setConsultModalOpen(true)}
                className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Request Consult
              </button>
            )}
            <span className="text-xs font-extrabold px-3 py-1.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
              DOCTORS ONLY
            </span>
          </div>
        </div>

        {/* Insight counter pill */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
            <span className="w-2 h-2 rounded-full bg-primary" />
            {responses.length} {responses.length === 1 ? 'insight' : 'insights'} shared
          </span>
        </div>

        {/* Discussion Thread */}
        <div className="space-y-4 max-h-[30rem] overflow-y-auto pr-1">
          {responses.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-outline-variant/30 rounded-2xl">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-7 h-7 text-slate-300" />
              </div>
              <p className="text-sm font-bold text-slate-800 mb-1">No insights yet</p>
              <p className="text-xs text-slate-500">Be the first to share your medical opinion</p>
            </div>
          ) : (
            responses.map((response, index) => {
              // --- Consult request entry ---
              if (response.type === 'consult_request') {
                const consultedDoc = response.consultedDoctorId;
                const authorId = response.doctorId?._id?.toString() || response.doctorId?.toString();
                const currentUserId = user?._id?.toString() || user?.id?.toString();
                const isMe = authorId && currentUserId && authorId === currentUserId;
                const requesterName = isMe ? 'You' : `Dr. ${response.doctorId?.name || 'Unknown'}`;
                return (
                  <div key={response._id || index} className="bg-violet-50 rounded-2xl p-5 border border-violet-100">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-100 text-violet-600">
                          <UserPlus className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-bold text-sm text-slate-900">
                            {requesterName} requested a consult from Dr.{' '}
                            {consultedDoc?.name || 'Unknown'}
                            {consultedDoc?.specialization && (
                              <span className="ml-1.5 text-xs font-extrabold px-2 py-0.5 bg-violet-100 text-violet-600 border border-violet-200 rounded-full">
                                {consultedDoc.specialization}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">{new Date(response.createdAt).toLocaleString()}</p>
                        </div>
                      </div>
                      <span className="text-xs font-extrabold px-3 py-1.5 rounded-full border uppercase bg-violet-100 text-violet-700 border-violet-200 flex-shrink-0">
                        CONSULT
                      </span>
                    </div>
                    {response.text && (
                      <p className="text-[13px] text-slate-500 ml-[52px] leading-relaxed font-medium">{response.text}</p>
                    )}
                  </div>
                );
              }
              // --- Regular insight entry ---
              const t = typeCfg[response.type] || typeCfg.observation;
              const TIcon = t.Icon;
              const authorId = response.doctorId?._id?.toString() || response.doctorId?.toString();
              const currentUserId = user?._id?.toString() || user?.id?.toString();
              const isMe = authorId && currentUserId && authorId === currentUserId;
              const name = isMe ? 'You' : (response.doctorId?.name || 'Unknown');
              const initials = isMe ? 'ME' : name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
              return (
                <div
                  key={response._id || index}
                  className="bg-slate-50 rounded-2xl p-5 border border-outline-variant/20 hover:bg-white hover:shadow-premium transition-all duration-500 group"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs ${isMe ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                        {initials}
                      </div>
                      <div>
                        <p className="font-headline font-bold text-sm text-slate-900 group-hover:text-primary transition-colors">
                          {isMe ? 'You' : `Dr. ${name}`}
                          {response.doctorId?.specialization && (
                            <span className="ml-2 text-xs font-extrabold px-2 py-0.5 bg-violet-50 text-violet-600 border border-violet-100 rounded-full">
                              {response.doctorId.specialization}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 font-semibold tracking-wider">
                          {new Date(response.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-extrabold px-3 py-1.5 rounded-full border uppercase ${t.bg} ${t.text} ${t.border}`}>
                      {response.type || 'observation'}
                    </span>
                  </div>
                  <p className="text-[13px] text-slate-500 whitespace-pre-wrap ml-[52px] leading-relaxed font-medium break-words">{response.text}</p>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add New Insight Form — footer area */}
      <form onSubmit={handleSubmit} className="px-7 pb-7 relative z-10 space-y-4">
        <div className="border-t border-outline-variant/10 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            <p className="text-xs font-extrabold text-primary uppercase tracking-[0.2em]">Add Your Insight</p>
          </div>
          <textarea
            value={newInsight}
            onChange={(e) => setNewInsight(e.target.value)}
            placeholder="Share your professional opinion, diagnosis, or suggestion..."
            className="w-full px-5 py-4 border border-outline-variant/20 bg-surface-container-low text-slate-900 placeholder-slate-400 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white text-sm transition-all resize-none"
            rows="3"
            maxLength="2000"
            required
          />
          <p className="text-xs text-slate-500 mt-1.5 font-semibold">
            {newInsight.length}/2000 characters
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Type:</label>
            <select
              value={insightType}
              onChange={(e) => setInsightType(e.target.value)}
              className="px-3 py-2 border border-outline-variant/20 bg-surface-container-low text-slate-800 rounded-xl focus:ring-2 focus:ring-primary/20 text-xs font-bold"
            >
              <option value="observation">Observation</option>
              <option value="diagnosis">Diagnosis</option>
              <option value="suggestion">Suggestion</option>
              <option value="question">Question</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={!newInsight.trim() || isSubmitting}
            className="px-6 py-3 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-primary transition-all shadow-sm hover:shadow-primary/25 disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed active:scale-[0.98]"
          >
            {isSubmitting ? 'Posting...' : 'Post Insight'}
          </button>
        </div>
      </form>
      <ConsultModal
        open={consultModalOpen}
        onClose={() => setConsultModalOpen(false)}
        symptomId={symptomId}
        excludeIds={consultedDoctorIds.map(id => id?.toString())}
        onSuccess={() => {
          setConsultModalOpen(false);
          onConsultSuccess?.();
        }}
      />
    </div>
  );
};

export default MedicalDiscussion;
